/**
 * The single boundary that maps between the canonical (current) loaded-state
 * shape the app speaks and the legacy shape the frozen v1–v9 codecs speak.
 *
 * - Decode: `legacyStateToCanonical` is applied to the output of every legacy
 *   decoder (see ../encodeDecodeState.ts), dropping `startContextSelectionType`
 *   and defaulting `startSet`.
 * - Encode: `canonicalStateToLegacy` synthesises the
 *   `startContextSelectionType` sentinel from `startQuery` presence. It has had
 *   no production caller since v10 became the canonical-native encoder; only
 *   `adapter.test.ts` exercises it.
 *
 * Keeping both directions here means the legacy/canonical translation is
 * decided once, not re-derived inside each historical codec.
 */
import {
  ComponentSelection as CanonicalComponentSelection,
  PersistedLoadedStateParams,
  isAnyPersistedTraversalLoadedState,
  isSearchLoadedState,
} from '@ardoq/api-types';
import {
  ComponentSelection as LegacyComponentSelection,
  LoadedStateParams as LegacyLoadedStateParams,
  StartContextSelectionType,
  isAnyTraversalLoadedState as isAnyLegacyTraversalLoadedState,
  isSearchLoadedState as isLegacySearchLoadedState,
} from './componentSelectionTypes';

const legacyComponentSelectionToCanonical = (
  componentSelection: LegacyComponentSelection
): CanonicalComponentSelection => ({
  startSet: componentSelection.startSet ?? [],
  ...(componentSelection.startQuery && {
    startQuery: componentSelection.startQuery,
  }),
});

export const legacyStateToCanonical = (
  state: LegacyLoadedStateParams
): PersistedLoadedStateParams => {
  if (isAnyLegacyTraversalLoadedState(state)) {
    return {
      ...state,
      data: {
        ...state.data,
        componentSelection: legacyComponentSelectionToCanonical(
          state.data.componentSelection
        ),
      },
    } as PersistedLoadedStateParams;
  }
  if (isLegacySearchLoadedState(state)) {
    return {
      ...state,
      data: {
        componentSelection: legacyComponentSelectionToCanonical(
          state.data.componentSelection
        ),
      },
    } as PersistedLoadedStateParams;
  }
  // CREATED_ITEMS carries no componentSelection — passes through unchanged.
  return state as PersistedLoadedStateParams;
};

const canonicalComponentSelectionToLegacy = (
  componentSelection: CanonicalComponentSelection
): LegacyComponentSelection => ({
  startSet: componentSelection.startSet,
  ...(componentSelection.startQuery && {
    startQuery: componentSelection.startQuery,
  }),
  // The legacy wire format carries a position-2 sentinel. The current model no
  // longer distinguishes BASIC vs ADVANCED select-all, and the legacy decoders
  // collapse both back to "has query", so emitting BASIC (2) for any query and
  // MANUAL (1) otherwise reproduces the byte losslessly.
  startContextSelectionType: componentSelection.startQuery
    ? StartContextSelectionType.BASIC_SEARCH_SELECT_ALL
    : StartContextSelectionType.MANUAL_SELECTION,
});

export const canonicalStateToLegacy = (
  state: PersistedLoadedStateParams
): LegacyLoadedStateParams => {
  if (isAnyPersistedTraversalLoadedState(state)) {
    return {
      ...state,
      data: {
        ...state.data,
        componentSelection: canonicalComponentSelectionToLegacy(
          state.data.componentSelection
        ),
      },
    } as LegacyLoadedStateParams;
  }
  if (isSearchLoadedState(state)) {
    return {
      ...state,
      data: {
        componentSelection: canonicalComponentSelectionToLegacy(
          state.data.componentSelection
        ),
      },
    } as LegacyLoadedStateParams;
  }
  return state as LegacyLoadedStateParams;
};
